import type { CastingReviewStatus, Contact, Gender, PaymentType } from "@/types";
import {
  buildCmsValidationSummary,
  type CmsValidationEntry,
} from "./cmsFormValidation";

export interface CastingContactForm {
  phone: string;
  landlinePhone: string;
  instagram: string;
  email: string;
  externalLink: string;
}

export interface CastingFormState {
  title: string;
  gender: Gender;
  fisic: string;
  description: string;
  requiredInfo: string;
  paymentType: PaymentType;
  reviewStatus: CastingReviewStatus;
  endDate: string;
  contact: CastingContactForm;
}

export type CastingFormField = keyof Omit<CastingFormState, "contact">;
export type CastingContactField = keyof CastingContactForm;

export const CASTING_FORM_FIELD_LABELS: Record<CastingFormField, string> = {
  title: "Título",
  gender: "Género",
  fisic: "Físico del rol",
  description: "Descripción",
  requiredInfo: "Información requerida",
  paymentType: "Tipo de pago",
  reviewStatus: "Estado",
  endDate: "Fecha de cierre",
};

export const CASTING_CONTACT_FIELDS: readonly CastingContactField[] = [
  "phone",
  "landlinePhone",
  "instagram",
  "email",
  "externalLink",
];

export const CONTACT_REQUIRED_MESSAGE =
  "Debes agregar al menos un medio de contacto.";

const CASTING_CONTACT_EMAIL_INVALID = "El email no es válido";

const CASTING_CONTACT_URL_INVALID =
  "Debe ser una URL válida (con http:// o https://)";

const CASTING_CONTACT_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidCastingContactEmail = (value: string): boolean =>
  CASTING_CONTACT_EMAIL_REGEX.test(value);

const isValidCastingContactExternalLink = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

/**
 * Validates a single contact field when it has content (matches backend ContactDto rules).
 */
export const validateCastingContactField = (
  field: CastingContactField,
  value: string,
): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (field === "email" && !isValidCastingContactEmail(trimmed)) {
    return CASTING_CONTACT_EMAIL_INVALID;
  }

  if (field === "externalLink" && !isValidCastingContactExternalLink(trimmed)) {
    return CASTING_CONTACT_URL_INVALID;
  }

  return null;
};

type CastingContactFieldColSpan = 1 | 2 | 3 | 4 | 5 | 6;

export const CASTING_CONTACT_FIELD_CONFIG: readonly {
  id: CastingContactField;
  placeholder?: string;
  label: string;
  type?: string;
  hint?: string;
  colSpan?: CastingContactFieldColSpan;
}[] = [
  {
    id: "phone",
    label: "WhatsApp",
    type: "tel",
    hint: "Número completo de WhatsApp",
    placeholder: "Ej: +5493511111111",
    colSpan: 3,
  },
  {
    id: "landlinePhone",
    label: "Teléfono para llamadas",
    type: "tel",
    hint: "Número para llamadas directas",
    placeholder: "Ej: 0351 4111111",
    colSpan: 3,
  },
  {
    id: "instagram",
    label: "Instagram",
    hint: "Perfil de Instagram al cual dirigirse para más información (sin @).",
    placeholder: "Ej: terceroespacio.cultura",
    colSpan: 3,
  },
  {
    id: "email",
    label: "Correo electrónico",
    type: "email",
    hint: "Correo electrónico al cual dirigirse enviar la info de inscripción.",
    placeholder: "Ej: usuario@example.com",
    colSpan: 3,
  },
  {
    id: "externalLink",
    label: "Enlace externo",
    type: "url",
    hint: "URL externa donde el usuario puede inscribirse al casting (con https:// o http://).",
    placeholder: "Ej: https://forms.google.com/inscripcion-casting",
    colSpan: 6,
  },
];

export const emptyCastingContact = (): CastingContactForm => ({
  phone: "",
  landlinePhone: "",
  instagram: "",
  email: "",
  externalLink: "",
});

export const emptyCastingForm = (
  reviewStatus: CastingReviewStatus = "PUBLISHED",
): CastingFormState => ({
  title: "",
  gender: "ANY",
  fisic: "",
  description: "",
  requiredInfo: "",
  paymentType: "FEES",
  reviewStatus,
  endDate: "",
  contact: emptyCastingContact(),
});

export const hasAtLeastOneCastingContact = (
  contact: CastingContactForm,
): boolean =>
  CASTING_CONTACT_FIELDS.some((field) => contact[field].trim().length > 0);

export const buildCastingContactPayload = (
  contact: CastingContactForm,
): Contact => {
  const result: Contact = {};

  const phone = contact.phone.trim();
  if (phone) result.phone = phone;

  const landlinePhone = contact.landlinePhone.trim();
  if (landlinePhone) result.landlinePhone = landlinePhone;

  const instagram = contact.instagram.trim();
  if (instagram) result.instagram = instagram;

  const email = contact.email.trim();
  if (email) result.email = email;

  const externalLink = contact.externalLink.trim();
  if (externalLink) result.externalLink = externalLink;

  return result;
};

export const validateCastingForm = (
  form: CastingFormState,
): {
  fieldErrors: Partial<Record<CastingFormField, string>>;
  contactError: string | null;
  contactFieldErrors: Partial<Record<CastingContactField, string>>;
} => {
  const fieldErrors: Partial<Record<CastingFormField, string>> = {};

  if (!form.title.trim()) fieldErrors.title = "Ingresá el título.";
  if (!form.description.trim()) {
    fieldErrors.description = "Ingresá la descripción.";
  }
  if (!form.requiredInfo.trim()) {
    fieldErrors.requiredInfo = "Ingresá la información requerida.";
  }
  if (!form.endDate) fieldErrors.endDate = "Ingresá la fecha de cierre.";

  const contactError = hasAtLeastOneCastingContact(form.contact)
    ? null
    : CONTACT_REQUIRED_MESSAGE;

  const contactFieldErrors: Partial<Record<CastingContactField, string>> = {};

  if (!contactError) {
    for (const field of CASTING_CONTACT_FIELDS) {
      const message = validateCastingContactField(field, form.contact[field]);
      if (message) contactFieldErrors[field] = message;
    }
  }

  return { fieldErrors, contactError, contactFieldErrors };
};

export type CastingFormValidationResult = ReturnType<typeof validateCastingForm>;

export const buildCastingValidationSummary = (
  result: CastingFormValidationResult,
): string | null => {
  const entries: CmsValidationEntry[] = Object.entries(result.fieldErrors)
    .filter((entry): entry is [CastingFormField, string] => Boolean(entry[1]))
    .map(([field, message]) => ({
      label: CASTING_FORM_FIELD_LABELS[field],
      message,
    }));

  if (result.contactError) {
    entries.push({
      label: "Medios de contacto",
      message: result.contactError,
    });
  }

  for (const field of CASTING_CONTACT_FIELDS) {
    const message = result.contactFieldErrors[field];
    if (!message) continue;

    const config = CASTING_CONTACT_FIELD_CONFIG.find((item) => item.id === field);
    entries.push({
      label: config?.label ?? field,
      message,
    });
  }

  return buildCmsValidationSummary(entries);
};

export const buildCastingPayload = (form: CastingFormState) => ({
  title: form.title.trim(),
  gender: form.gender,
  fisic: form.fisic.trim(),
  description: form.description.trim(),
  requiredInfo: form.requiredInfo.trim(),
  paymentType: form.paymentType,
  reviewStatus: form.reviewStatus,
  endDate: form.endDate,
  contact: buildCastingContactPayload(form.contact),
});
